import { useEffect, useRef, useState } from 'react';

import { TUserPreference, TUserWithConnect } from '@cloud-wave/neon-common-lib';
import { ConstValues } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';

import { useLocalStorage } from 'lib/common/hooks/useLocalStorage';

import { LogEvents, logger } from 'lib/common/components/LoggerController';

import { SIGN_OUT_EVENT } from 'lib/common/constants/signOutEvent';

import { CURRENT_USER_FULLNAME_KEY } from '../constants';
import SIGN_OUT_TYPES from '../constants/signOutTypes';
import { AuthStages } from '../types/AuthState';

type Fetch = (url: string, options?: RequestInit) => Promise<Response>;

export const useInitializeUser = ({
  authStage,
  fetch_,
  setError,
  signOut,
  setEmail,
  setLoaded,
  connectUserId,
}: {
  authStage: keyof typeof AuthStages;
  fetch_: Fetch;
  setError: (error: boolean) => void;
  signOut: (signOutType: ConstValues<typeof SIGN_OUT_TYPES>) => void;
  setEmail: (email: string) => void;
  setLoaded: (loaded: boolean) => void;
  connectUserId?: string;
}) => {
  const { config } = useConfigContext();
  const { removeStorageItem } = useLocalStorage();
  const [user, setUser] = useState<TUserWithConnect | null>(null);
  const [userPreferences, setUserPreferences] = useState<TUserPreference | null>(null);

  // Allows callbacks to get the latest user to avoid stale references, eg. in connect handlers
  const userRef = useRef(user);
  const getUser = () => userRef?.current;

  const initializeUser = async () => {
    const AGENT_URL = `${config.AGENT_SERVICE_V2_URL}/user-data`;

    try {
      const response = await fetch_(AGENT_URL);
      const {
        data: { user, userPreferences },
      }: { data: { user: TUserWithConnect; userPreferences: TUserPreference } } = await response.json();

      if (!user) {
        throw new Error('No user data found');
      }

      setUser(user);
      setUserPreferences(userPreferences);

      sessionStorage.setItem('user_id', user.id);
      sessionStorage.setItem(CURRENT_USER_FULLNAME_KEY, `${user.firstName} ${user.lastName}`);

      // If the user has previously signed out and this event exists in storage, remove it on sign in
      removeStorageItem(SIGN_OUT_EVENT);
      setEmail(user.email);

      setLoaded(true);
      logger.info(LogEvents.AUTH.INITIALIZE_USER.SUCCESS);

      if (!connectUserId) {
        return;
      }

      // Logging users out after session invalid
      connect.core.getEventBus().subscribe(connect.EventType.AUTH_FAIL, () => {
        logger.warn(LogEvents.AUTH.SESSION_EXPIRED, { user: user?.email });

        signOut(SIGN_OUT_TYPES.AUTH_FAIL);
      });
    } catch (error: any) {
      logger.info(LogEvents.AUTH.INITIALIZE_USER.FAIL, { error });
      setError(true);
    }
  };

  useEffect(() => {
    if (authStage.includes('complete')) {
      initializeUser();
    }
  }, [authStage]);

  useEffect(() => {
    userRef.current = user;
  }, [user, authStage]);

  return { user, getUser, userPreferences };
};
